import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import RadioListConfirmPopover, { RadioListConfirmPopoverProps } from '@components/RadioListConfirmPopover'
import { useSortProfileValuesMap } from '@modules/shortlist/hooks/useSortProfileValuesMap'
import { ShortlistSortProfileValue } from '@modules/shortlist/types'

type FieldsToExcludeFromParentProps =
  | 'title'
  | 'confirmBtnText'
  | 'confirmDisabled'
  | 'cancelBtnText'
  | 'value'
  | 'onChange'
  | 'items'

export interface SortPopoverProps extends Omit<RadioListConfirmPopoverProps, FieldsToExcludeFromParentProps> {
  sortProfileValues: ShortlistSortProfileValue[]
  value: Nullable<ShortlistSortProfileValue>
  onChange: (sortOptionValue: ShortlistSortProfileValue) => void
  title?: string
}

const SortPopover = ({ sortProfileValues, value, title, onChange, onConfirm, onClose, onCancel, ...rest }: SortPopoverProps) => {
  const { t } = useTranslation()

  const sortProfileValuesMap = useSortProfileValuesMap()

  const [localValue, setLocalValue] = useState<Nullable<ShortlistSortProfileValue>>(value)

  const items = useMemo(
    () =>
      sortProfileValues.map<RadioListConfirmPopoverProps['items'][0]>(sortOptionValue => ({
        label: sortProfileValuesMap[sortOptionValue].label,
        value: sortOptionValue,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortProfileValues],
  )

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleConfirm = useCallback(() => {
    if (localValue && localValue !== value) {
      onChange(localValue)
    }

    onConfirm()
  }, [onChange, onConfirm, localValue, value])

  const handleChange = useCallback((v: string) => {
    setLocalValue(v as ShortlistSortProfileValue)
  }, [])

  const handleClose = useCallback(() => {
    setLocalValue(value)
    onClose?.()
  }, [value, onClose])

  const handleCancel = useCallback(() => {
    setLocalValue(value)
    onCancel?.()
  }, [value, onCancel])

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <RadioListConfirmPopover
      title={t(title ?? translationKeys.CUSTOM_LIST_TABLE_SORT_CATEGORY)}
      confirmBtnText={t(translationKeys.CUSTOM_LIST_POPOVER_CANCEL_BTN)}
      onConfirm={handleConfirm}
      cancelBtnText={t(translationKeys.CUSTOM_LIST_POPOVER_CONFIRM_BTN)}
      value={localValue}
      onChange={handleChange}
      items={items}
      onClose={handleClose}
      onCancel={handleCancel}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  )
}

export default memo(SortPopover)
